import { useMemo, useState } from 'react';
import { Upload } from 'antd';
import { CloseOutlined, FileOutlined, InboxOutlined } from '@ant-design/icons';

import { Badge, Button, Checkbox, Dialog, Field } from '../../../components/ui';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { dungLuongVN, soVN } from '../../../lib/dinh-dang';
import { loiTheoTruong } from '../../../lib/errors';
import { useLuaChonModelAi } from '../../ai-model/hooks/useAiModels';
import { KET_QUA_TAI } from '../constants';
import { ChonModelDocChu } from './ChonModelDocChu';
import { useGhiKhoOcr } from '../hooks/useKhoOcr';
import { kiemTep, modelDocChu, modelDocChuMacDinh, TOI_DA_SO_TEP } from '../nghiep-vu/kho-ocr';

const khoaTep = (f: File) => `${f.name}:${f.size}:${f.lastModified}`;

/**
 * Hộp tải tài liệu lên kho để đọc chữ trước (`POST /ocr-files/upload`). Tệp kiểm ngay khi chọn
 * (PDF và ảnh, 25 MB mỗi tệp, tối đa 20 tệp), lỗi hiện dưới đúng tệp. Gửi xong **không** đóng hộp:
 * backend trả kết quả từng tệp (vào hàng đợi, bỏ qua, không nhận) và người dùng cần đọc kết quả đó.
 *
 * Phía gọi gắn `key` mới mỗi lần mở để hộp bắt đầu trống.
 */
export function HopTaiLen({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const luaChon = useLuaChonModelAi();
  const { taiLen } = useGhiKhoOcr();
  const [tep, setTep] = useState<File[]>([]);
  const [model, setModel] = useState<string | undefined>();
  const [boQua, setBoQua] = useState(true);
  const [daThuGui, setDaThuGui] = useState(false);

  const docChu = useMemo(() => modelDocChu(luaChon.data ?? []), [luaChon.data]);
  const modelChon = model ?? modelDocChuMacDinh(luaChon.data ?? []);
  const kiem = kiemTep(tep);
  const loiTep = new Map(kiem.theoTep.map((x) => [x.chiSo, x]));
  const loiServer = loiTheoTruong(taiLen.error);
  const ketQua = taiLen.data?.results;

  const gui = () => {
    setDaThuGui(true);
    if (!kiem.hopLe || !modelChon) return;
    taiLen.mutate({ files: tep, reader_key: modelChon, skip_if_done: boQua });
  };

  const tomTat = ketQua && {
    xep: ketQua.filter((r) => r.outcome === 'QUEUED').length,
    bo: ketQua.filter((r) => r.outcome === 'SKIPPED_DONE' || r.outcome === 'SKIPPED_RUNNING').length,
    loi: ketQua.filter((r) => r.outcome === 'REJECTED' || r.outcome === 'FAILED').length,
  };

  const loiChung =
    taiLen.error && Object.keys(loiServer).length === 0 ? dichLoi(taiLen.error) : (loiServer['/files'] ?? null);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t(ketQua ? 'khoOcr.ketQuaTieuDe' : 'khoOcr.hopTaiTieuDe')}
      width={600}
      destroyOnHidden
      footer={
        <div className="flex flex-wrap justify-end gap-3">
          {ketQua ? (
            <Button variant="primary" size="md" onClick={onClose}>
              {t('khoOcr.xong')}
            </Button>
          ) : (
            <>
              <Button variant="outline" size="md" onClick={onClose}>
                {t('chung.huy')}
              </Button>
              <Button variant="primary" size="md" loading={taiLen.isPending} onClick={gui}>
                {taiLen.isPending ? t('khoOcr.dangGui') : t('khoOcr.guiSo', { so: soVN(tep.length, locale) })}
              </Button>
            </>
          )}
        </div>
      }
    >
      {ketQua && tomTat ? (
        <div className="space-y-3 pt-2">
          <p role="status" className="text-sm text-content-secondary">
            {t('khoOcr.ketQuaTomTat', { xep: tomTat.xep, bo: tomTat.bo, loi: tomTat.loi })}
          </p>
          <ul className="divide-y divide-[var(--border-default)] rounded border border-default">
            {ketQua.map((r, i) => (
              <li key={`${r.file_name}-${i}`} className="flex flex-col gap-1 px-3 py-2.5">
                <div className="flex min-w-0 items-center justify-between gap-3">
                  <span className="min-w-0 truncate text-sm text-content-primary" title={r.file_name}>
                    {r.file_name}
                  </span>
                  <Badge variant={KET_QUA_TAI[r.outcome].variant}>{t(KET_QUA_TAI[r.outcome].khoa)}</Badge>
                </div>
                {r.message && <p className="text-xs text-content-muted">{r.message}</p>}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="space-y-4 pt-2">
          <Field
            label={t('khoOcr.tepDaChon')}
            required
            error={
              daThuGui && tep.length === 0
                ? t('khoOcr.loiChuaChon')
                : kiem.quaNhieuTep
                  ? t('khoOcr.loiQuaNhieu', { so: tep.length - TOI_DA_SO_TEP })
                  : undefined
            }
          >
            {(p) => (
              <div className="space-y-2">
                <Upload.Dragger
                  multiple
                  showUploadList={false}
                  accept="application/pdf,image/*"
                  beforeUpload={(f) => {
                    setTep((cu) => (cu.some((x) => khoaTep(x) === khoaTep(f)) ? cu : [...cu, f]));
                    return false;
                  }}
                >
                  <div id={p.id} aria-describedby={p['aria-describedby']} className="flex flex-col items-center gap-2 px-4 py-5">
                    <InboxOutlined className="text-2xl text-content-muted" aria-hidden />
                    <p className="text-sm font-medium text-content-primary">{t('khoOcr.keoTha')}</p>
                    <p className="text-xs text-content-muted">{t('khoOcr.keoThaMo')}</p>
                  </div>
                </Upload.Dragger>
                {tep.length > 0 && (
                  <ul className="max-h-56 divide-y divide-[var(--border-default)] overflow-y-auto rounded border border-default">
                    {tep.map((f, i) => {
                      const loi = loiTep.get(i);
                      return (
                        <li key={khoaTep(f)} className="flex items-start gap-2 px-3 py-2">
                          <FileOutlined className="mt-1 shrink-0 text-content-muted" aria-hidden />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm text-content-primary" title={f.name}>
                              {f.name}
                            </p>
                            <p className="text-2xs text-content-muted">{dungLuongVN(f.size, locale)}</p>
                            {loi && (
                              <p role="alert" className="mt-0.5 text-xs text-danger-600">
                                {t(loi.loi === 'qua-lon' ? 'khoOcr.loiQuaLon' : 'khoOcr.loiSaiDinhDang', { ten: f.name })}
                              </p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={t('khoOcr.boTep', { ten: f.name })}
                            title={t('khoOcr.boTep', { ten: f.name })}
                            icon={<CloseOutlined />}
                            onClick={() => setTep((cu) => cu.filter((x) => x !== f))}
                          />
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </Field>

          <ChonModelDocChu
            ds={docChu}
            dangTai={luaChon.isLoading}
            value={modelChon}
            onChange={setModel}
            error={loiServer['/reader_key'] ?? (daThuGui && !modelChon && docChu.length > 0 ? t('khoOcr.loiChuaChonModel') : undefined)}
          />

          <div>
            <Checkbox checked={boQua} onChange={(e) => setBoQua(e.target.checked)}>
              <span className="text-sm text-content-secondary">{t('khoOcr.boQuaDaCo')}</span>
            </Checkbox>
            <p className="ml-6 mt-0.5 text-xs text-content-muted">{t('khoOcr.boQuaDaCoMo')}</p>
          </div>

          {loiChung && <LoiTrongHop>{loiChung}</LoiTrongHop>}
        </div>
      )}
    </Dialog>
  );
}
